import { useState } from "react";
import { Link } from "react-router-dom";
import { Anchor, ArrowLeft, Apple, Smartphone, Sparkles, Camera, FileText, Users, Anchor as AnchorIcon } from "lucide-react";

/**
 * Public beta landing page (`/beta`).
 *
 * Captains land here from social posts / boating Facebook groups and pick
 * their platform. Both install URLs are stored as constants so the founder
 * can update them once the TestFlight public link + Play Console internal
 * tester link are minted from the respective consoles.
 *
 * If a URL is null we fall back to a "Request invite" mailto so we don't
 * lose interested testers while the links are being set up.
 */
/**
 * Install link priority (highest → lowest). The card picks the first one that's
 * set; nulls fall through. Once you launch publicly, just flip `PLAY_STORE_LIVE`
 * (or `APP_STORE_LIVE`) to `true` — the deterministic store URLs below take
 * over automatically and the "Request invite" mailto retires itself.
 */
// Tier 1 — Public store URLs (work once the app is live on the production track):
const PLAY_STORE_LIVE = false;                                                       // flip → true after Play Store approval
const APP_STORE_LIVE = false;                                                        // flip → true after App Store approval
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=app.bosunsmate"; // deterministic — only used when PLAY_STORE_LIVE
const APP_STORE_URL = null;                                                          // paste once Apple assigns a numeric ID, e.g. "https://apps.apple.com/app/id1234567890"

// Tier 2 — Closed/internal testing URLs (fallback while the public listing is in review):
const TESTFLIGHT_URL = null;       // e.g. "https://testflight.apple.com/join/XXXXXXXX"
const PLAY_INTERNAL_URL = null;    // e.g. "https://play.google.com/apps/internaltest/XXXXXXXXXXXXX"

// Tier 3 — Contact email (final fallback so we never lose an interested captain):
const CONTACT_EMAIL = "captain@bosunsmate.app";

// Resolved install links — exported for the cards below.
const IOS_LINK = (APP_STORE_LIVE && APP_STORE_URL) ? APP_STORE_URL : TESTFLIGHT_URL;
const ANDROID_LINK = (PLAY_STORE_LIVE && PLAY_STORE_URL) ? PLAY_STORE_URL : PLAY_INTERNAL_URL;
const IOS_PUBLIC = !!(APP_STORE_LIVE && APP_STORE_URL);
const ANDROID_PUBLIC = !!(PLAY_STORE_LIVE && PLAY_STORE_URL);

const FEATURES = [
    { icon: AnchorIcon, title: "Multi-vessel log", body: "Powerboats, sailboats, charter — separate captain's log for each, switch in a tap." },
    { icon: Camera, title: "AI receipt scan", body: "Snap a fuel dock receipt. We pull vendor, amount, and date automatically." },
    { icon: FileText, title: "PDF export", body: "Full vessel history as a clean PDF — survey, insurance, resale, all in one." },
    { icon: Users, title: "Share with crew", body: "Invite a co-captain or your mechanic. Roles for viewer / editor / admin." },
];

export default function Beta() {
    const subject = encodeURIComponent("Bosun's Mate — beta tester request");
    const body = encodeURIComponent(
        "Hi Sean,\n\nI'd like to help beta-test Bosun's Mate.\n\n" +
        "Platform (iOS / Android / both):\n" +
        "Boat I'd be logging:\n" +
        "Google account email (for Android testing — if applicable):\n\n" +
        "Thanks!"
    );
    const mailto = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;

    return (
        <div data-testid="beta-page" style={{ minHeight: "100vh", background: "var(--bm-bg)", color: "var(--bm-text)" }}>
            {/* Header */}
            <header style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "22px 32px", maxWidth: 1200, margin: "0 auto",
            }}>
                <Link to="/" data-testid="beta-brand-link" style={{
                    display: "inline-flex", alignItems: "center", gap: 10,
                    textDecoration: "none", color: "inherit",
                }}>
                    <Anchor size={20} color="var(--bm-gold)" />
                    <span className="bm-display" style={{ fontSize: 24 }}>Bosun's Mate</span>
                </Link>
                <Link to="/" data-testid="beta-home-link" className="bm-link" style={{
                    display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13,
                }}>
                    <ArrowLeft size={14} /> Home
                </Link>
            </header>

            {/* Hero */}
            <section style={{
                maxWidth: 920, margin: "0 auto",
                padding: "40px 32px 32px",
                textAlign: "center",
            }}>
                <div style={{
                    display: "inline-flex", alignItems: "center", gap: 8,
                    padding: "6px 14px", borderRadius: 999,
                    border: "1px solid var(--bm-gold-dim)",
                    color: "var(--bm-gold)", fontSize: 12,
                    letterSpacing: "0.16em", textTransform: "uppercase",
                    marginBottom: 24,
                }}>
                    <Sparkles size={12} /> Beta · Limited spots
                </div>
                <h1 className="bm-display" style={{
                    fontSize: "clamp(38px, 6vw, 64px)",
                    lineHeight: 1.05, margin: 0, fontWeight: 500,
                }}>
                    Help shape the captain's log <br /><em style={{ color: "var(--bm-gold)" }}>every boater wishes they had.</em>
                </h1>
                <p style={{
                    margin: "22px auto 0", maxWidth: 580,
                    fontSize: 17, lineHeight: 1.55,
                    color: "var(--bm-text-muted)",
                }}>
                    Bosun's Mate tracks maintenance, fuel economy, expenses, surveys, anchorages and crew across every vessel you own.
                    We're inviting a small group of captains to test the iOS + Android apps before public launch.
                </p>
            </section>

            {/* Install CTAs */}
            <section style={{
                maxWidth: 760, margin: "0 auto",
                padding: "12px 32px 56px",
            }}>
                <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                    gap: 16,
                }}>
                    <PlatformCard
                        testid="beta-ios-card"
                        icon={Apple}
                        platform="iOS"
                        cta={
                            IOS_PUBLIC ? "Get on the App Store"
                            : IOS_LINK ? "Install via TestFlight"
                            : "Request iOS invite"
                        }
                        href={IOS_LINK || mailto}
                        external={!!IOS_LINK}
                        sub={
                            IOS_PUBLIC ? "Bosun's Mate is live on the App Store. One-tap install."
                            : IOS_LINK ? "Free Apple TestFlight app required — instructions in-app."
                            : "We'll email you a TestFlight link within a day."
                        }
                    />
                    <PlatformCard
                        testid="beta-android-card"
                        icon={Smartphone}
                        platform="Android"
                        cta={
                            ANDROID_PUBLIC ? "Get it on Google Play"
                            : ANDROID_LINK ? "Install via Play Store"
                            : "Request Android invite"
                        }
                        href={ANDROID_LINK || mailto}
                        external={!!ANDROID_LINK}
                        sub={
                            ANDROID_PUBLIC ? "Bosun's Mate is live on Google Play. One-tap install."
                            : ANDROID_LINK ? "Opens Play Store. Accept the test, then 'Download on Google Play'."
                            : "Send us the Google account email you'd use to install."
                        }
                    />
                </div>
                <p style={{
                    textAlign: "center", marginTop: 18,
                    fontSize: 13, color: "var(--bm-text-dim)",
                }}>
                    Already a captain on the web? Visit <Link to="/login" className="bm-link">bosunsmate.app/login</Link> — your data syncs across all three.
                </p>
            </section>

            {/* What you get */}
            <section style={{
                maxWidth: 1100, margin: "0 auto",
                padding: "8px 32px 56px",
            }}>
                <div style={{ textAlign: "center", marginBottom: 32 }}>
                    <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--bm-gold)", marginBottom: 10 }}>
                        What's in the box
                    </div>
                    <h2 className="bm-serif" style={{ fontSize: "clamp(26px, 3.5vw, 34px)", margin: 0, fontWeight: 500 }}>
                        A quiet, beautiful place for every boat thing.
                    </h2>
                </div>
                <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 16,
                }}>
                    {FEATURES.map((f) => (
                        <div key={f.title} data-testid={`beta-feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`} className="bm-card" style={{ padding: "22px 20px" }}>
                            <f.icon size={20} color="var(--bm-gold)" />
                            <h3 className="bm-serif" style={{ margin: "12px 0 6px", fontSize: 19, fontWeight: 500 }}>{f.title}</h3>
                            <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: "var(--bm-text-muted)" }}>{f.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* What we need from you */}
            <section style={{
                maxWidth: 760, margin: "0 auto",
                padding: "8px 32px 56px",
            }}>
                <div className="bm-card" style={{ padding: "28px 28px" }}>
                    <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--bm-gold)", marginBottom: 10 }}>
                        What we ask of you
                    </div>
                    <h2 className="bm-serif" style={{ fontSize: 24, margin: "0 0 14px", fontWeight: 500 }}>
                        Use it on your boat. Tell us what's wrong.
                    </h2>
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14.5, lineHeight: 1.75, color: "var(--bm-text)" }}>
                        <li>Log <strong>one real entry</strong> — a maintenance task, a fuel fill, an anchorage.</li>
                        <li>Try the Cruising Log on your next trip.</li>
                        <li>If something breaks, surprises you, or annoys you — <a className="bm-link" href={`mailto:${CONTACT_EMAIL}?subject=Bosun%27s%20Mate%20feedback`}>email us</a>.</li>
                        <li>Stay in the test for at least 14 days (Google Play requirement).</li>
                    </ul>
                    <p style={{ margin: "18px 0 0", fontSize: 13, color: "var(--bm-text-muted)" }}>
                        Beta is free. When we launch, every beta captain gets <strong style={{ color: "var(--bm-gold)" }}>three months on us</strong> as thanks.
                    </p>
                </div>
            </section>

            {/* Footer */}
            <footer style={{
                borderTop: "1px solid var(--bm-border-soft)",
                padding: "28px 32px 40px",
                textAlign: "center",
                color: "var(--bm-text-dim)", fontSize: 12,
            }}>
                Built in Whitehorse, Yukon · <Link to="/privacy" className="bm-link">Privacy</Link> · <Link to="/terms" className="bm-link">Terms</Link> · <a href={`mailto:${CONTACT_EMAIL}`} className="bm-link">{CONTACT_EMAIL}</a>
            </footer>
        </div>
    );
}

function PlatformCard({ testid, icon: Icon, platform, cta, href, external, sub }) {
    const isMailto = href.startsWith("mailto:");
    return (
        <a
            data-testid={testid}
            href={href}
            target={external ? "_blank" : undefined}
            rel={external ? "noopener noreferrer" : undefined}
            className="bm-card"
            style={{
                padding: "26px 24px",
                textDecoration: "none",
                display: "flex", flexDirection: "column", gap: 10,
                color: "inherit",
                transition: "transform 0.18s ease, border-color 0.18s ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.borderColor = "var(--bm-gold-dim)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.borderColor = ""; }}
        >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Icon size={26} color="var(--bm-gold)" />
                <span className="bm-display" style={{ fontSize: 24 }}>{platform}</span>
            </div>
            <span data-testid={`${testid}-cta`} className="bm-btn bm-btn-gold" style={{
                padding: "11px 16px", fontSize: 14, justifyContent: "center",
                marginTop: 6,
            }}>
                {cta}
            </span>
            <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5, color: "var(--bm-text-muted)" }}>
                {sub}
            </p>
            {isMailto && (
                <p style={{ margin: 0, fontSize: 11, color: "var(--bm-text-dim)" }}>
                    Opens your email app pre-filled — just hit send.
                </p>
            )}
        </a>
    );
}
